import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface InvalidField {
  controlName: string;
  displayName: string;
}

@Injectable({
  providedIn: 'root'
})
export class FormValidationTrackerService {
  private invalidFieldsSubject = new BehaviorSubject<InvalidField[]>([]);
  public invalidFields$: Observable<InvalidField[]> = this.invalidFieldsSubject.asObservable();

  private fieldDisplayNames: Map<string, string> = new Map([
    // Customer form fields
    ['customerFirstName', 'Customer First Name'],
    ['customerLastName', 'Customer Last Name'],
    ['customerName', 'Customer First and Last Name'],
    ['customerNameORcontractNumber', 'Contract Number OR Customer First and Last Name'],
    ['contractNumber', 'Contract Number'],
    ['vin', 'Last 6 digits of VIN'],
    
    // Mechanical breakdown form fields
    ['breakdownDate', 'Breakdown Date'],
    ['currentOdometer', 'Current Mileage'],
    ['repairOrderNumber', 'Repair Order Number'],
    ['transportationMode', 'Driven or Towed In'],
    
    // Concern/Cause form fields
    ['concern', 'Customer Concern'],
    ['component', 'Failed Component'],
    ['cause', 'Cause of Failure'],
    
    // Estimate line item fields (base names - will be prefixed with line numbers)
    ['partNumber', 'Part Number'],
    ['etaDays', 'Part ETA'],
    ['price', 'Price'],
    ['quantity', 'Quantity'],
    ['laborOperation', 'Labor Operation'],
    ['rate', 'Labor Rate'],
    ['hours', 'Hours'],
    ['description', 'Description']
  ]);

  /**
   * Update the list of invalid fields
   * @param invalidFields Array of invalid field control names
   */
  public updateInvalidFields(invalidFields: string[]): void {
    const mappedFields: InvalidField[] = invalidFields.map(controlName => ({
      controlName,
      displayName: this.fieldDisplayNames.get(controlName) || controlName
    }));
    
    this.invalidFieldsSubject.next(mappedFields);
  }

  /**
   * Clear all invalid fields
   */
  public clearInvalidFields(): void {
    this.invalidFieldsSubject.next([]);
  }

  /**
   * Get current invalid fields
   */
  public getCurrentInvalidFields(): InvalidField[] {
    return this.invalidFieldsSubject.value;
  }

  /**
   * Add a custom field display name mapping
   * @param controlName The form control name
   * @param displayName The human-readable display name
   */
  public addFieldDisplayName(controlName: string, displayName: string): void {
    this.fieldDisplayNames.set(controlName, displayName);
  }

  /**
   * Get display name for a control
   * @param controlName The form control name
   * @returns The human-readable display name
   */
  public getDisplayName(controlName: string): string {
    return this.fieldDisplayNames.get(controlName) || controlName;
  }
}